import { useState } from 'react';
import { SideListCard, type SideListItem } from '@practium/ui';
import styles from './RegionsSidebar.module.css';
import { BrazilMapSVG } from '../../Map/BrazilMap/BrazilMapSVG';
import { RegionMap } from '../../Map/RegionMap/RegionMap';
import { hasMapboxToken } from '../../../utils/mapboxUtils';
import type { RegionDTO } from '../../../types/parameters.types';
import { CreateRegionModal } from '../../Modals/CreateRegionModal/CreateRegionModal';

const hasMap = hasMapboxToken();

interface RegionsSidebarProps {
	regions: RegionDTO[];
	selectedRegion: RegionDTO | null;
	isLoading: boolean;
	isCreateModalOpen: boolean;
	onSelectRegion: (region: RegionDTO) => void;
	onCreateRegion: (name: string, stateCodes: string[]) => void;
	onUpdateRegion: (regionId: string, name: string, stateCodes: string[]) => void;
	onCloseCreateModal: () => void;
}

export const RegionsSidebar = ({
	regions,
	selectedRegion,
	isLoading,
	isCreateModalOpen,
	onSelectRegion,
	onCreateRegion,
	onUpdateRegion,
	onCloseCreateModal,
}: RegionsSidebarProps) => {
	const [modalMode, setModalMode] = useState<'modify' | null>(null);

	const handleCreateConfirm = (name: string, stateCodes: string[]) => {
		onCreateRegion(name, stateCodes);
		onCloseCreateModal();
	};

	const handleModifyConfirm = (name: string, stateCodes: string[]) => {
		if (selectedRegion) {
			onUpdateRegion(selectedRegion._id, name, stateCodes);
		}
		setModalMode(null);
	};

	const items: SideListItem[] = regions.map((region) => ({
		id: region._id,
		label: region.name,
		icon: (
			<div className={styles.regionMapThumb}>
				<BrazilMapSVG selectedStateCodes={region.stateCodes} />
			</div>
		),
	}));

	return (
		<>
			<SideListCard
				title="Todas as Regiões"
				subtitle="Selecione a região que deseja visualizar"
				items={items}
				activeId={selectedRegion?._id}
				onSelect={(id) => {
					const region = regions.find((r) => r._id === id);
					if (region) onSelectRegion(region);
				}}
				actionButton={{
					label: 'Modificar',
					onClick: () => setModalMode('modify'),
					disabled: !selectedRegion,
				}}
				isLoading={isLoading}
				stretch={!hasMap}
			/>

			{selectedRegion && <RegionMap region={selectedRegion} />}

			<CreateRegionModal
				isOpen={isCreateModalOpen}
				regions={regions}
				onClose={onCloseCreateModal}
				onConfirm={handleCreateConfirm}
			/>

			<CreateRegionModal
				isOpen={modalMode === 'modify'}
				regions={regions}
				initialRegion={selectedRegion ?? undefined}
				onClose={() => setModalMode(null)}
				onConfirm={handleModifyConfirm}
			/>
		</>
	);
};
